import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useContextSelector as useCS } from 'use-context-selector';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { TUpdate } from 'domain/types';
import { Button, CompanyAvatar } from 'view/components';
import { useCreateJobPost } from 'view/pages/profile/hooks/useCreateJobPost';
import { CompanySelector } from 'view/pages/createJobPost/components/CompanySelector';
import { CreateCompanyModal } from 'view/pages/createJobPost/components/CreateCompanyModal';
import { Membership } from 'types';

const CompanySetup = () => {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // null until the recruiter picks a company — nothing is selected by default.
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);

  const { isLoading: isCreating, createJobPost } = useCreateJobPost();

  const userData: any = useCS(GlobalConfigContext, globalConfigSelectors.userData);
  // recruiterType drives the company field: individual picks any company, representative is bound.
  const recruiterType = userData?.recruiterProfile?.recruiterType || 'representative';
  const isIndividual = recruiterType === 'individual';

  const isOpenBackToHomeModal: boolean = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.isOpenBackToHomeModal
  );

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  // Representative's company is bound via membership; individual doesn't need one.
  const {
    data: memberships,
    isLoading: isLoadingMemberships,
  }: {
    data: Membership[];
    isLoading: boolean;
  } = useRequest({
    url: '/api/v1/memberships/me',
    method: ApiMethodsEnum.Get,
    sendOnStart: true,
  });

  useEffect(() => {
    if (!isOpenBackToHomeModal) return;
    if (update) update({ isOpenBackToHomeModal: false });
    navigate('/candidates/candidates-list');
  }, [isOpenBackToHomeModal, update, navigate]);

  // The array guard is needed because useRequest yields null before the response (and on request
  // failure), even though the annotation promises Membership[].
  const allMemberships = Array.isArray(memberships) ? memberships : [];

  // Only what can actually be picked is listed, so the list and the gating are the same set: a
  // verified membership with a verified work email on an ACTIVE company. The company check is what
  // keeps the backend's "Unmapped (legacy)" placeholder (SUSPENDED) out of the list — a verified
  // membership alone doesn't mean the company can be posted for.
  const postableMemberships = allMemberships.filter((membership) => {
    return (
      membership.verificationStatus === 'VERIFIED' &&
      membership.workEmailVerified &&
      membership.company?.status === 'ACTIVE'
    );
  });

  // Nothing is selected by default — the company is chosen explicitly. The selection is kept as an
  // id and resolved on every render against the postable set, so a refetch (a revoked membership,
  // a suspended company) can't leave an entry selected that may no longer be posted from.
  const selectedMembership =
    postableMemberships.find((membership) => {
      return membership.id === selectedMembershipId;
    }) || null;

  const boundCompany = selectedMembership?.company || null;

  // Individual can only post for an ACTIVE company — a freshly created one is PENDING_REVIEW and
  // must be admin-approved first (guide §8), so we block posting instead of creating a draft.
  const isIndividualCompanyPending =
    isIndividual && !!selectedCompany && selectedCompany.status !== 'ACTIVE';

  const canProceed = isIndividual
    ? !!selectedCompany && selectedCompany.status === 'ACTIVE'
    : !!boundCompany;

  const handleCreateJobPost = () => {
    // Individual binds the chosen company; representative binds the membership company. For the
    // representative the backend forces its own bound company and ignores what we send (guide §2),
    // so passing the id is safe and also covers backends that require it.
    const company = isIndividual ? selectedCompany : boundCompany;
    return createJobPost(company ? { company: { id: company.id } } : {});
  };

  // Only the representative branch depends on the memberships request.
  if (!isIndividual && isLoadingMemberships) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        px: 2,
        pt: 6,
        display: 'flex',
        flexDirection: 'column',
        // Cap the page at the viewport so the only scrollable area is the list below and the
        // title and the submit button stay put. The layout gives no definite height to inherit
        // (Wrapper only sets minHeight: 100vh), so the cap is the viewport minus the paddings
        // above us: Wrapper 2rem + RightBlock 1.5rem, and 0 + 1rem below `tablet`.
        maxHeight: {
          mobile: 'calc(100vh - 32px)',
          tablet: 'calc(100vh - 112px)',
        },
      }}
    >
      <Box sx={{ mb: 4, flexShrink: 0 }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600 }}>Select a Company</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
          Choose the company you want to post a job for.
        </p>
      </Box>

      {isIndividual ? (
        <>
          <CompanySelector
            value={selectedCompany}
            onChange={setSelectedCompany}
            onCreateNew={() => {
              setIsCreateModalOpen(true);
            }}
          />
          <CreateCompanyModal
            open={isCreateModalOpen}
            onClose={() => {
              setIsCreateModalOpen(false);
            }}
            onCreated={(company: any) => {
              setSelectedCompany(company);
            }}
          />
          {isIndividualCompanyPending && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The selected company is pending review and cannot be used until an administrator
              approves it.
            </Alert>
          )}
        </>
      ) : (
        <>
          {postableMemberships.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  // The one scrolling element on the page: `auto` basis keeps a short list
                  // hugging its content, and once the page hits its cap this is what shrinks
                  // and scrolls instead of pushing the button out of view.
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  // Keeps the card borders off the scrollbar track.
                  pr: '4px',

                  // Same thin scrollbar as the other wizard steps (see commonStyled.ts).
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#D8DBE8 transparent',

                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#D8DBE8',
                    borderRadius: '10px',
                    transition: 'background 0.2s ease-in-out',
                  },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#BFC3D4' },
                  '&::-webkit-scrollbar-thumb:active': { background: '#A5AABF' },
                }}
              >
                {postableMemberships.map((membership) => {
                  const isSelected = membership.id === selectedMembership?.id;

                  return (
                    <Box
                      key={membership.id}
                      onClick={() => {
                        setSelectedMembershipId(membership.id);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        p: '20px',
                        // Cards keep their height when the list overflows — otherwise the column
                        // flex would compress them instead of letting the list scroll.
                        flexShrink: 0,
                        // Same border width in both states so cards don't shift when selected.
                        border: `2px solid ${isSelected ? '#663497' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        bgcolor: 'white',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s',

                        '&:hover': {
                          borderColor: isSelected ? '#663497' : '#c4b5d4',
                        },
                      }}
                    >
                      <CompanyAvatar company={membership.company} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Company</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '16px', fontWeight: 600 }}>
                          {membership.company.name}
                        </p>
                      </Box>
                      {isSelected && <CheckCircleIcon sx={{ color: '#663497' }} />}
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            // Having memberships but none usable is a different case from having none at all: the
            // list hides the unusable ones, so this is the only place that tells the recruiter why
            // the company they just joined isn't here.
            <Alert severity="info">
              {allMemberships.length > 0 ? (
                <>
                  None of your company memberships can be used for posting yet — they are awaiting
                  approval or their company is unavailable. Go to{' '}
                  <Link to="/profile/company-memberships">Company Memberships</Link> to check their
                  status.
                </>
              ) : (
                <>
                  You don&apos;t have a company membership yet. Go to{' '}
                  <Link to="/profile/company-memberships">Company Memberships</Link> to join or
                  create one.
                </>
              )}
            </Alert>
          )}
        </>
      )}

      {/* With no postable membership there is nothing to post for — the list and its alert above
          are all that shows. The button stays visible but disabled until a company is picked. */}
      {(isIndividual || postableMemberships.length > 0) && (
        <Box sx={{ mt: 4, pb: 2, flexShrink: 0 }}>
          <Button
            variant="contained"
            text="Create Job Post"
            onClick={handleCreateJobPost}
            disabled={!canProceed || isCreating}
            style={{ width: '100%', minHeight: '50px' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CompanySetup;
